import React, {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import {
  BackResponse,
  BackResponseFailure,
  MessageType,
  Timeslot,
} from '@savgroup-front-common/types';

import Button from '../../atoms/button/Button';
import { SafeFormattedMessageWithoutSpread } from '../../formatters/SafeFormattedMessageWithoutSpread';
import { ChevronLeftIcon } from '../../protons/icons';
import { $ActionRow } from '../InterventionAppointment/components/TakeInterventionAppointment/AppointmentCalendarContainer/AppointmentCalendarContainer.styles';

import {
  $AppointmentCalendar,
  $ButtonWithMinWidth,
  AppointmentCalendarContainer,
  LeftArrowButton,
  RightArrowButton,
} from './AppointmentCalendar.styles';
import { AppointmentCalendarLoader } from './AppointmentCalendarLoader';
import { AppointmentCalendarScreen } from './AppointmentCalendarScreen';
import messages from './messages';
import { DetailedSchedule } from './types/DetailedSchedule';

interface AppointmentCalendarProps {
  isActive?: boolean;
  isGetTimeslotsLoading?: boolean;
  isSubmitLoading: boolean;
  isLoading?: boolean;
  startTime?: Date;
  onValidateSchedule?: ({
    selectedTimeslot,
  }: {
    selectedTimeslot?: Timeslot;
  }) => Promise<BackResponse<any> | BackResponseFailure>;

  customLoader?: ReactElement;
  messageList?: MessageType[];
  schedules: DetailedSchedule[];
  onPageNextClick?: () => void;
  onPagePreviousClick?: () => void;
  hasErrors?: boolean;
  isFirstPage?: boolean;
  onPreviousStepClick?: () => void;
}

const AppointmentCalendar: FC<
  React.PropsWithChildren<AppointmentCalendarProps>
> = ({
  children,
  isActive = true,
  isGetTimeslotsLoading = false,
  startTime,
  isSubmitLoading,
  isLoading = false,
  onValidateSchedule,

  customLoader,
  messageList,
  schedules,
  onPageNextClick,
  onPagePreviousClick,
  hasErrors,
  isFirstPage = false,
  onPreviousStepClick,
}) => {
  const [selectedTimeslot, setSelectedTimeslot] = useState<
    Timeslot | undefined
  >();
  const [isSubmitDone, setIsSubmitDone] = useState(false);

  const isCurrentWeekEmpty = (schedules || []).every(
    ({ timeslots = [] }) =>
      timeslots.filter((timeslot) => !!timeslot).length === 0,
  );
  const isValidateScheduleDisabled = !schedules || isGetTimeslotsLoading;

  const handleValidateSchedule = useCallback(
    async ({ selectedTimeslot }: { selectedTimeslot?: Timeslot }) => {
      setIsSubmitDone(true);

      if (onValidateSchedule) {
        const response = await onValidateSchedule({ selectedTimeslot });

        if (response.failure) {
          setIsSubmitDone(false);
        }
      }
    },
    [onValidateSchedule],
  );

  useEffect(() => {
    if (hasErrors) {
      setIsSubmitDone(false);
    }
  }, [hasErrors]);

  if (isGetTimeslotsLoading) {
    return (
      <AppointmentCalendarLoader
        customLoader={customLoader}
        messageList={messageList}
      />
    );
  }

  return (
    <$AppointmentCalendar data-testid="AppointmentCalendar">
      <AppointmentCalendarContainer>
        <$ButtonWithMinWidth
          type={BUTTON_TYPES.BUTTON}
          onClick={onPagePreviousClick}
          icon={<LeftArrowButton />}
          disabled={isFirstPage}
          dataTestId="previousScreenButton"
          primary
          naked
          small
        />
        <AppointmentCalendarScreen
          isActive={isActive}
          selectedTimeslot={selectedTimeslot}
          onTimeslotSelect={({ selectedTimeslot }) => {
            setSelectedTimeslot(selectedTimeslot);
          }}
          schedules={schedules}
          startTime={startTime}
        />
        <$ButtonWithMinWidth
          type={BUTTON_TYPES.BUTTON}
          onClick={onPageNextClick}
          icon={<RightArrowButton />}
          dataTestId="nextScreenButton"
          primary
          naked
          small
        />
      </AppointmentCalendarContainer>
      <$ActionRow>
        {children}
        {onPreviousStepClick && (
          <Button
            onClick={onPreviousStepClick}
            tertiary
            naked
            type={BUTTON_TYPES.BUTTON}
            icon={<ChevronLeftIcon />}
          >
            <SafeFormattedMessageWithoutSpread message={messages.back} />
          </Button>
        )}

        {!isCurrentWeekEmpty && (
          <Button
            disabled={isValidateScheduleDisabled || !selectedTimeslot}
            isLoading={isLoading || isSubmitLoading || isSubmitDone}
            primary
            type={BUTTON_TYPES.BUTTON}
            isError={hasErrors}
            onClick={() => handleValidateSchedule({ selectedTimeslot })}
            dataTestId="validateSchedule"
          >
            <SafeFormattedMessageWithoutSpread message={messages.validate} />
          </Button>
        )}
      </$ActionRow>
    </$AppointmentCalendar>
  );
};

export default AppointmentCalendar;
